"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, UserCheck, HardHat, FileCheck2, ArrowRight, CircleCheck as CheckCircle2, Award, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const accountabilityPillars = [{
  title: "Direct contracts management",
  description: "You deal directly with experienced leadership from initial site survey through erection, handover, and dismantling.",
  icon: UserCheck,
  tag: "Single point of contact"
}, {
  title: "20+ years of hands-on leadership",
  description: "Pete Wood oversees operational delivery, ensuring practical problem-solving and rigorous site discipline on every scaffold.",
  icon: HardHat,
  tag: "Experienced oversight"
}, {
  title: "Family-run values & genuine care",
  description: "Carrying forward Adam Story's legacy, our team prioritises reliability, respectful site conduct, and honest communication.",
  icon: ShieldCheck,
  tag: "Personal responsibility"
}, {
  title: "NASC, CHAS & Constructionline",
  description: "Fully audited health and safety procedures, documented handover certificates, and full compliance with TG20 guidelines.",
  icon: Award,
  tag: "Accredited standards"
}];
const accreditationBadges = [{
  name: "NASC Full Member",
  detail: "Audited safety standards"
}, {
  name: "CHAS Accredited",
  detail: "Health & safety compliant"
}, {
  name: "Constructionline",
  detail: "Pre-qualified contractor"
}];
const leadershipCommitments = ["Direct phone access to contracts management throughout your build", "Site-specific risk assessments and method statements (RAMS) for every project", "TG20-compliant design and certified scaffold handover documentation", "Trained CISRS scaffolders dedicated to neat, secure, and prompt site works"];
export default function Accountability() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="leadershipaccountability" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 border-primary/30 text-foreground bg-primary/5">
            Leadership & Safety
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Direct accountability on every scaffold
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            Pete Wood leads every project with over 20 years of hands-on experience, bringing the care of a family-run business and accredited safety standards to your site.
          </p>
        </div>

        {/* Main Grid: Core Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {accountabilityPillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} data-index={i} initial={shouldReduceMotion ? undefined : {
            opacity: 0,
            y: 16
          }} whileInView={shouldReduceMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="h-full">
                <Card className="h-full border border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                  <CardContent className="p-6 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-4">
                        <div className="size-11 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                          <Icon className="size-5" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-sm">
                          {pillar.tag}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground font-default mb-2">
                        {pillar.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {pillar.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Feature Focus Box: Pete Wood & Team Commitment */}
        <div className="rounded-xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            {/* Left Content Area */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                <FileCheck2 className="size-4" />
                <span>BMF Scaffolding Management Standard</span>
              </div>
              
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default leading-tight">
                No middle layers. Direct leadership from quote to dismantle.
              </h3>

              <p className="text-muted-foreground leading-relaxed">
                Whether you are managing a complex commercial development, a high-spec domestic refurbishment, or council maintenance, we believe clear responsibility matters. Pete Wood personally ensures each scaffold meets our high internal benchmark and strictly complies with NASC criteria.
              </p>

              {/* Commitments Checklist */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {leadershipCommitments.map((item, index) => <div key={index} data-index={index} className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground">{item}</span>
                  </div>)}
              </div>

              <Separator className="my-4" />

              {/* Call to action buttons */}
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                    <span>Request a site consultation</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                
                <Button asChild variant="outline" size="lg">
                  <Link to="/HealthSafetyAccreditationsPage" className="inline-flex items-center gap-2">
                    <span>View accreditations</span>
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Card: Accreditations & Key Stats */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              <div className="rounded-lg bg-muted/60 p-6 border border-border/80">
                <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                  Verified Industry Accreditations
                </h4>
                <div className="space-y-3">
                  {accreditationBadges.map((acc, idx) => <div key={acc.name} data-index={idx} className="flex items-center justify-between p-3 rounded-md bg-card border border-border">
                      <div className="flex items-center gap-3">
                        <div className="size-2 rounded-full bg-primary" />
                        <div>
                          <p className="text-sm font-semibold text-foreground font-default">{acc.name}</p>
                          <p className="text-xs text-muted-foreground">{acc.detail}</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs font-normal">
                        Verified
                      </Badge>
                    </div>)}
                </div>
              </div>

              <div className="rounded-lg bg-secondary text-secondary-foreground p-5 flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-secondary-foreground/70 font-medium">Direct Inquiries</p>
                  <p className="text-sm font-semibold mt-0.5">Speak with our operations team</p>
                </div>
                <Button asChild variant="outline" size="sm" className="bg-transparent text-secondary-foreground border-secondary-foreground/30 hover:bg-secondary-foreground/10 hover:text-secondary-foreground">
                  <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-1.5">
                    <PhoneCall className="size-3.5" />
                    <span>Get in touch</span>
                  </Link>
                </Button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}